import React, { useState, useEffect } from "react";
import UserModal from "./UserModal";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Download,
  Filter,
  Users,
  List,
  LayoutGrid,
  ArrowUpAZ,
  ArrowDownZA,
  User,
  Mail,
  Phone,
  Shield,
  Building2,
  CheckCircle,
  XCircle,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  Eye,
  EyeOff,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getUserList, UpdateUserInfo } from "@/redux/Masters/users/actions";
import { callLoader } from "@/redux/Loader/actions";
import { validateForm } from "../ui/useFormValidation";
import { showToast } from "@/redux/Toast/actions";

// ─── Animated title ────────────────────────────────────────────
const TITLE = "Maintain Users";
function AnimatedTitle() {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: "0ch" }}
        animate={{
          width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"],
        }}
        transition={{
          duration: 4.8,
          times: [0, 0.22, 0.9, 1],
          ease: ["linear", "linear", "easeInOut"],
          repeat: Infinity,
        }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

// ─── Status Badge ──────────────────────────────────────────────
function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      <CheckCircle className="w-3 h-3" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
      <XCircle className="w-3 h-3" /> Inactive
    </span>
  );
}

// ─── Avatar ────────────────────────────────────────────────────
// FIX: show first+last initials instead of PROFILEIMAGE[0]
// (PROFILEIMAGE is not returned in the list API, so it's always undefined in the table)
function UserAvatar({ user }) {
  const initials =
    [user?.FIRSTNAME?.[0], user?.LASTNAME?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() ||
    user?.USERNAME?.[0]?.toUpperCase() ||
    "U";

  // Show image for any non-empty string (http, https, relative paths, /uploads/...)
  if (
    user?.PROFILEIMAGE &&
    typeof user.PROFILEIMAGE === "string" &&
    user.PROFILEIMAGE.trim() !== ""
  ) {
    return (
      <img
        src={user.PROFILEIMAGE}
        alt={initials}
        className="w-8 h-8 rounded-lg object-cover border border-slate-200 dark:border-slate-600 shrink-0"
        onError={(e) => {
          // If image fails to load, replace with initials avatar
          e.target.replaceWith(
            Object.assign(document.createElement("div"), {
              className:
                "w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm select-none",
              textContent: initials,
            }),
          );
        }}
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm select-none">
      {initials}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────
const MaintainUsers = () => {
  const dispatch = useDispatch();
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];

  const initialForm = {
    id: "0",
    createdbyuser: authUser?.USERNAME,
    modifiedbyuser: authUser?.USERNAME,
    companY_ID: authUser?.COMPANYID,
    FIRSTNAME: "",
    LASTNAME: "",
    EMAILID: "",
    USERNAME: "",
    PASSWORD: "",
    CONTACTNO: "",
    ROLEID: "",
    OFFICEID: "",
    OfficeType: "",
    Profitcenter: "",
    profitType: "",
    profit: "",
    salesperson: false,
    isActive: true,
    status: true,
    PROFILEIMAGE: "",
    IS_FILEMANAGER: false,
  };

  // ── State ──────────────────────────────────────────────────
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [view, setView] = useState("table");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const { usersList, usersPagination } = useSelector((state) => state.users);
  console.log("usersList: ", usersList);
  const { roleList } = useSelector((state) => state.common);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    _getList(1);
  }, [searchText, recordsPerPage]);

  const _getList = async (page = 1) => {
    setCurrentPage(page);
    dispatch(callLoader(1));
    try {
      await dispatch(
        getUserList({
          iPageNo: page,
          iPageRecords: recordsPerPage,
          SearchKeyword: searchText || "",
        }),
      );
    } finally {
      dispatch(callLoader(0));
    }
  };

  // ── Handlers ───────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddNew = () => {
    setFormData(initialForm);
    setErrors({});
    setSelectedVessel(null);
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      id: row?.USERID?.toString() || "0",
      companY_ID: row?.COMPANYID || authUser?.COMPANYID,
      FIRSTNAME: row?.FIRSTNAME || "",
      LASTNAME: row?.LASTNAME || "",
      EMAILID: row?.EMAILID || "",
      USERNAME: row?.USERNAME || "",
      PASSWORD: row?.PASSWORD || "",
      CONTACTNO: row?.CONTACTNO || "",
      ROLEID: row?.ROLEID?.toString() || "",
      OFFICEID: row?.OFFICEID?.toString() || "",
      OfficeType: row?.OFFICETYPENAME || "",
      Profitcenter: row?.PROFITCENTER || "",
      profitType: row?.PROFITTYPE || "",
      profit: row?.PROFIT || "",
      salesperson: row?.IS_SALESPERSON ?? false,
      isActive: row?.ISACTIVE ?? true,
      status: row?.ISACTIVE ?? true,
      IS_FILEMANAGER: row?.IS_FILEMANAGER ?? true,
      PROFILEIMAGE: row?.PROFILEIMAGE || "",
      createdbyuser: authUser?.USERNAME,
      modifiedbyuser: authUser?.USERNAME,
    });
    setErrors({});
    setSelectedVessel(row);
    setOpenModal(true);
  };

  const handleSaveUser = (isEdit = false) => {
    _getList(isEdit ? currentPage : 1);
  };

  const handlePrevious = () => {
    if (currentPage > 1) _getList(currentPage - 1);
  };
  const handleNext = () => {
    if (usersPagination?.nextPage > 0) _getList(usersPagination.nextPage);
  };

  // ── Filtered list ──────────────────────────────────────────
  const filteredList = (usersList || []).filter((item) => {
    const s = searchText.toLowerCase();
    if (!s) return true;
    return (
      item?.FIRSTNAME?.toLowerCase().includes(s) ||
      item?.LASTNAME?.toLowerCase().includes(s) ||
      item?.USERNAME?.toLowerCase().includes(s) ||
      item?.EMAILID?.toLowerCase().includes(s)
    );
  });

  // ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-full space-y-3 p-2 sm:p-3 lg:p-4">
      {/* ════ HEADER CARD ════════════════════════════════════ */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800">
          {/* Left */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-100 dark:ring-blue-800 flex items-center justify-center">
              <Users
                className="w-5 h-5 text-blue-600 dark:text-blue-400"
                strokeWidth={2.2}
              />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400 hidden sm:block truncate max-w-sm lg:max-w-lg">
                Manage system users, roles, and access permissions
              </p>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              onClick={handleAddNew}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-[12.5px] font-semibold shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="xs:inline">Create New User</span>
            </button>
          </div>
        </div>
      </div>

      {/* ════ FILTER BAR ════════════════════════════════════ */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative flex items-center gap-2">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
            >
              <option value="ALL">All Fields</option>
              <option value="NAME">Name</option>
              <option value="EMAIL">Email</option>
              <option value="OFFICE">Office</option>
              <option value="USERNAME">Username</option>
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                // FIX: "off" + non-credential name prevents browser from treating
                // this as a username login field and autocompleting it
                autoComplete="off"
                name="users-search-field"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Sort */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() =>
                  setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
                }
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {sortDirection === "asc" ? (
                  <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                ) : (
                  <ArrowDownZA className="w-4 h-4 text-gray-500" />
                )}
              </button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="recent">Most Recent</option>
                <option value="name">Full Name</option>
                <option value="username">Username</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results summary */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {usersPagination?.currentRecord || filteredList.length}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {usersPagination?.totalRecord || filteredList.length}
            </span>{" "}
            users
          </p>
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              className="text-[12px] text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ════ CONTENT ════════════════════════════════════════ */}
      <div className="p-1">
        {filteredList.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
            <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">
              No users found
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={handleAddNew}
              className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              + Add New User
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                    {[
                      { label: "#", cls: "w-12 text-center" },
                      { label: "User", cls: "min-w-[200px]" },
                      { label: "Contact", cls: "min-w-[180px]" },
                      { label: "Role", cls: "min-w-[100px]" },
                      { label: "Username", cls: "min-w-[120px]" },
                      { label: "Password", cls: "min-w-[100px] text-center" },
                      { label: "Status", cls: "min-w-[100px]" },
                    ].map((h) => (
                      <th
                        key={h.label}
                        className={`px-4 py-3.5 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-300 ${h.cls}`}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  <AnimatePresence>
                    {filteredList.map((user, idx) => {
                      const rowKey = user?.USERID ?? user?.USERNAME ?? idx;
                      const isPwdVisible = visiblePasswords[rowKey];

                      return (
                        <motion.tr
                          key={rowKey}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 8 }}
                          transition={{ delay: idx * 0.03, duration: 0.2 }}
                          className={[
                            "group transition-colors cursor-default",
                            idx % 2 === 0
                              ? "bg-white dark:bg-gray-900"
                              : "bg-slate-50/60 dark:bg-gray-800/30",
                            "hover:bg-blue-50/40 dark:hover:bg-blue-900/10",
                          ].join(" ")}
                        >
                          {/* # */}
                          <td className="px-4 py-3 text-center">
                            <span className="text-[11px] font-bold text-slate-400 tabular-nums">
                              {(currentPage - 1) * recordsPerPage + idx + 1}
                            </span>
                          </td>

                          {/* User */}
                          <td className="px-4 py-3">
                            <div
                              onClick={() => handleEdit(user)}
                              className="flex items-center gap-3 cursor-pointer group"
                            >
                              {/* Avatar */}
                              <UserAvatar user={user} />

                              {/* User Info */}
                              <div className="min-w-0">
                                <p className="font-semibold text-[13px] text-slate-900 dark:text-white truncate transition-colors group-hover:text-blue-600">
                                  {user?.FIRSTNAME} {user?.LASTNAME}
                                </p>

                                <p className="text-[11px] text-slate-400 font-mono truncate transition-colors group-hover:text-blue-500">
                                  @{user?.USERNAME}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Contact */}
                          <td className="px-4 py-3">
                            <div className="space-y-0.5">
                              <p className="text-[12px] text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                <Mail className="w-3 h-3 text-blue-400 shrink-0" />
                                <span className="truncate max-w-[140px]">
                                  {user?.EMAILID || "—"}
                                </span>
                              </p>
                              <p className="text-[12px] text-slate-500 flex items-center gap-1.5">
                                <Phone className="w-3 h-3 text-green-400 shrink-0" />
                                {user?.CONTACTNO || "—"}
                              </p>
                            </div>
                          </td>

                          {/* Role */}
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300">
                              <Shield className="w-3 h-3" />
                              {user?.ROLETYPE || `#${user?.ROLEID || "—"}`}
                            </span>
                          </td>

                          {/* Username */}
                          <td className="px-4 py-3 text-left">
                            <span className="text-[12px] font-mono text-slate-600 dark:text-slate-400">
                              {user?.USERNAME}
                            </span>
                          </td>

                          {/* Password */}
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setVisiblePasswords((prev) => ({
                                  ...prev,
                                  [rowKey]: !prev[rowKey],
                                }))
                              }
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold"
                              aria-label={
                                isPwdVisible ? "Hide password" : "Show password"
                              }
                            >
                              {isPwdVisible ? (
                                <EyeOff className="w-3 h-3 text-red-500" />
                              ) : (
                                <Eye className="w-3 h-3 text-green-400" />
                              )}
                              {isPwdVisible
                                ? user?.PASSWORD
                                : "*****************"}
                            </button>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            <StatusBadge active={user?.ISACTIVE} />
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════ usersPagination FOOTER ════════════════════════════ */}
        {filteredList.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-800 px-4 pt-4 pb-8 mt-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between rounded-b-xl">
            {/* Per-page */}
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Show</span>
              <select
                value={recordsPerPage}
                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </div>

            {/* Page controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-default font-semibold">
                {currentPage}
              </button>
              <button
                onClick={handleNext}
                disabled={
                  !usersPagination?.nextPage || usersPagination.nextPage === 0
                }
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-medium">
                {usersPagination?.currentRecord || filteredList.length}
              </span>{" "}
              of{" "}
              <span className="font-medium">
                {usersPagination?.totalRecord || filteredList.length}
              </span>{" "}
              users
            </p>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      <UserModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedVessel(null);
          setFormData(initialForm);
          setErrors({});
        }}
        roleList={roleList}
        formData={formData}
        errors={errors}
        handleInputChange={handleInputChange}
        onSave={() => handleSaveUser(!!selectedVessel)}
        editData={selectedVessel}
      />
    </div>
  );
};

export default MaintainUsers;
